import React, { createContext, useContext, useEffect, useState } from 'react';
import { getDashboard } from '../services/userService';

interface AuthContextType {
  isLoggedIn: boolean;
  hasSubscription: boolean;
  authLoading: boolean;
  loginUser: (token: string) => void;
  logoutUser: () => void;
  setSubscriptionStatus: (value: boolean) => void;
}

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [token, setToken] = useState<string | null>(() => localStorage.getItem('token'));
  const [hasSubscription, setHasSubscription] = useState(false);
  const [authLoading, setAuthLoading] = useState(false);

  useEffect(() => {
    const hydrateSubscription = async () => {
      if (!token) {
        setHasSubscription(false);
        setAuthLoading(false);
        return;
      }

      setAuthLoading(true);
      try {
        const response = await getDashboard(token);
        const hasActiveSubscription = response.data.subscriptions.some((sub) => {
          const status = (sub.status || '').toLowerCase();
          return !['cancelled', 'expired', 'inactive'].includes(status);
        });
        setHasSubscription(hasActiveSubscription);
      } catch {
        setHasSubscription(false);
      } finally {
        setAuthLoading(false);
      }
    };

    hydrateSubscription();
  }, [token]);

  const loginUser = (t: string) => {
    localStorage.setItem('token', t);
    setToken(t);
  };

  const logoutUser = () => {
    localStorage.removeItem('token');
    setToken(null);
    setHasSubscription(false);
  };

  return (
    <AuthContext.Provider
      value={{
        isLoggedIn: !!token,
        hasSubscription,
        authLoading,
        loginUser,
        logoutUser,
        setSubscriptionStatus: setHasSubscription,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
